import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

const DATA_COLLECTED = [
  'Your WhatsApp phone number',
  'The content of the messages you send and receive',
  'Your WhatsApp display name or profile information, where visible',
  'The Building Passport account or building record your chat relates to',
]

const WhatsAppPrivacyExplainer = ({ label = 'Data Privacy Notice', variant = 'pill' }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <Wrap $variant={variant}>
      {variant === 'link' ? (
        <LinkTrigger
          type='button'
          aria-label='Open the WhatsApp data privacy notice'
          aria-expanded={open}
          onClick={e => { e.stopPropagation(); setOpen(true) }}
        >
          {label}
        </LinkTrigger>
      ) : (
        <InfoButton
          type='button'
          aria-label='Open the WhatsApp data privacy notice'
          aria-expanded={open}
          onClick={e => { e.stopPropagation(); setOpen(true) }}
        >
          <InfoOutlinedIcon style={{ fontSize: 15 }} />
          <span>{label}</span>
        </InfoButton>
      )}

      {open && createPortal(
        <Backdrop
          onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) setOpen(false) }}
        >
          <Dialog role='dialog' aria-modal='true' aria-label='WhatsApp chat with Guru — data privacy notice'>
            <Header>
              <HeaderTitle>WhatsApp Chat with Guru — Data Privacy Notice</HeaderTitle>
              <CloseBtn type='button' aria-label='Close' onClick={() => setOpen(false)}>
                <CloseRoundedIcon style={{ fontSize: 18 }} />
              </CloseBtn>
            </Header>

            <Body>
              <Lead>
                Building Passport (BP) offers an optional feature that lets you connect
                WhatsApp to chat with Guru, our AI assistant, for quick answers about your
                building records. This notice explains what happens to your data if you
                choose to use it.
              </Lead>

              <Section>
                <SectionTitle>Is this optional?</SectionTitle>
                <SectionText>
                  Yes. WhatsApp is never connected automatically. You must actively choose to
                  connect it (for example, by tapping “Connect WhatsApp” in your account
                  settings), and you can disconnect at any time from the same settings menu.
                </SectionText>
              </Section>

              <Section>
                <SectionTitle>What data we collect</SectionTitle>
                <SectionText>When you message Guru over WhatsApp, we collect:</SectionText>
                <BulletList>
                  {DATA_COLLECTED.map(item => (
                    <Bullet key={item}>{item}</Bullet>
                  ))}
                </BulletList>
              </Section>

              <Section>
                <SectionTitle>How your data is used</SectionTitle>
                <SectionText>
                  Your messages are sent from WhatsApp to Building Passport’s servers, then
                  passed to Guru, which is built on Anthropic’s Claude AI model, to generate a
                  response. Your data is used only to answer your query. It is not used to
                  train or improve any AI model.
                </SectionText>
              </Section>

              <Section>
                <SectionTitle>Where your data is stored</SectionTitle>
                <SectionText>
                  Your WhatsApp chat data is processed and stored within the UK/EU. It is not
                  transferred outside the UK/EU.
                </SectionText>
              </Section>

              <Section>
                <SectionTitle>How long we keep it</SectionTitle>
                <SectionText>
                  Chat messages and any data generated during your WhatsApp conversation with
                  Guru are automatically deleted after 30 minutes.
                </SectionText>
              </Section>

              <Section>
                <SectionTitle>Your rights and how to disconnect</SectionTitle>
                <SectionText>
                  You can disconnect WhatsApp from your account at any time in your account
                  settings. This stops any further data being shared with Guru.
                </SectionText>
                <SectionText>
                  If you would like any previously stored chat data deleted, or have any other
                  question about how your data is handled, contact us at{' '}
                  <MailLink href='mailto:info@buildingpassport.com'>info@buildingpassport.com</MailLink>.
                </SectionText>
              </Section>

              <Section>
                <SectionTitle>Who is responsible for your data</SectionTitle>
                <SectionText>
                  Building Passport Limited is the data controller for the personal data
                  processed through this feature.
                </SectionText>
              </Section>
            </Body>
          </Dialog>
        </Backdrop>,
        document.body
      )}
    </Wrap>
  )
}

export default WhatsAppPrivacyExplainer

const Wrap = styled.span`
  display: ${({ $variant }) => ($variant === 'link' ? 'inline' : 'inline-flex')};
`

const LinkTrigger = styled.button`
  font: inherit;
  display: inline;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(12, 18, 41, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 4px 11px 4px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease;
  &:hover { background: rgba(12, 18, 41, 0.06); }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100100;
  background: rgba(12, 18, 41, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 560px;
  max-width: 100%;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.28);
`

const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f2;
`

const HeaderTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  font: inherit;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 8px;
  cursor: pointer;
  opacity: 0.7;
  &:hover { opacity: 1; background: rgba(12, 18, 41, 0.05); }
`

const Body = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 22px 24px;
`

const Lead = styled.p`
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.6;
  color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const Section = styled.div`
  margin-top: 18px;
`

const SectionTitle = styled.h5`
  margin: 0 0 5px;
  font-size: 14.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const SectionText = styled.p`
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.6;
  color: #4d4d56;
`

const BulletList = styled.ul`
  margin: 4px 0 0;
  padding-left: 20px;
  list-style: disc;
`

const Bullet = styled.li`
  font-size: 14px;
  line-height: 1.6;
  color: #4d4d56;
  margin-bottom: 3px;
`

const MailLink = styled.a`
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 600;
  text-decoration: underline;
`
